import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import {toast} from "react-toastify";
import Breadcrumbs from "../../../components/breadcrumbs";
import {error_message, export_report} from "../../../redux/reports";
import {format_cell} from "./utils";
import "./reports.scss";

/** @module Pages/Reports/Layout */

const TABS = [
    {path: "/reportes/colaborador", label: "Por colaborador"},
    {path: "/reportes/calendario", label: "Calendario"},
    {path: "/reportes/centro-costo", label: "Por centro de costo"},
];

const FORMATS = [
    {formato: "xlsx", label: "Excel", icon: "bx bxs-file"},
    {formato: "csv", label: "CSV", icon: "bx bx-spreadsheet"},
    {formato: "pdf", label: "PDF", icon: "bx bxs-file-pdf"},
];

/**
 * Menú de exportación: descarga el reporte con los mismos filtros de la página.
 * @param {string} token
 * @param {object} body - {type, filters}
 */
export const ExportMenu = ({token, body}) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const download = async (formato) => {
        setOpen(false);
        setBusy(true);
        try {
            await export_report(token, body, formato);
        } catch (e) {
            toast.error(await error_message(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pbi-export">
            <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => setOpen(!open)}>
                <i className={busy ? "bx bx-loader-alt bx-spin" : "bx bx-download"}/> Exportar
            </button>
            {open ? (
                <div className="pbi-export__menu">
                    {FORMATS.map((f) => (
                        <button type="button" key={f.formato} onClick={() => download(f.formato)}>
                            <i className={f.icon}/> {f.label}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

/**
 * Contenedor común de las páginas de la reportería: título, exportación, lienzo del Power BI y
 * pestañas de página.
 */
export const ReportLayout = ({title, token, body, children, loading, error}) => (
    <div className="app-content content">
        <div className="content-overlay"/>
        <div className="content-wrapper">
            <div className="content-header row">
                <div className="content-header-left col-12 mb-2 mt-1">
                    <Breadcrumbs title="Reporte ingresos" items={[{label: title, link: "#"}]}/>
                </div>
            </div>
            <div className="content-body">
                <div className="card pbi-report">
                    <div className="card-content">
                        <div className="card-body">
                            <div className="pbi-toolbar">
                                <span className="pbi-status">
                                    {loading ? <><i className="bx bx-loader-alt bx-spin"/> Actualizando...</> : null}
                                </span>
                                <ExportMenu token={token} body={body}/>
                            </div>
                            {error ? <div className="alert alert-danger mb-1">{error}</div> : null}
                            <div className="pbi-canvas">{children}</div>
                            <nav className="pbi-tabs">
                                {TABS.map((tab) => (
                                    <NavLink key={tab.path} to={tab.path} className="pbi-tabs__tab" activeClassName="is-active">
                                        {tab.label}
                                    </NavLink>
                                ))}
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Tabla con el aspecto del visual "Tabla" del Power BI. Muestra los primeros 500 registros y
 * permite cargar más.
 * @param {object} data - Respuesta de la API ({columns, rows, total})
 * @param {object} widths - Ancho en px por clave de columna (como en el Power BI)
 */
export const PbiTable = ({data, widths, loading, onLoadMore}) => {
    if (!data) return <div className="pbi-table pbi-table--empty">{loading ? "Cargando..." : ""}</div>;
    return (
        <div className="pbi-table">
            <div className="pbi-table__scroll">
                <table>
                    <colgroup>
                        {data.columns.map((c) => <col key={c.key} style={widths[c.key] ? {width: widths[c.key]} : undefined}/>)}
                    </colgroup>
                    <thead>
                    <tr>{data.columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                    {data.rows.length === 0 ? (
                        <tr><td colSpan={data.columns.length} className="pbi-table__empty">No hay datos para los filtros seleccionados</td></tr>
                    ) : data.rows.map((row, index) => (
                        <tr key={index}>
                            {data.columns.map((c) => <td key={c.key}>{format_cell(row[c.key], c.type)}</td>)}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="pbi-table__footer">
                <span>{data.rows.length.toLocaleString("es-CL")} de {data.total.toLocaleString("es-CL")} filas</span>
                {data.rows.length < data.total ? (
                    <button type="button" className="btn btn-sm btn-light" disabled={loading} onClick={onLoadMore}>
                        Cargar más
                    </button>
                ) : null}
            </div>
        </div>
    );
};
